import {useEffect, useState} from "react";
import {Link, Navigate, useSearchParams} from "react-router-dom";
import {makeStyles} from "@mui/styles";
import {useAuth} from "../../../contexts/AuthContext";
import TrackApplicationCard from "../../../common/SACC/CareersFairContext/TrackApplicationCard";
import {formatDate, getDisplayStatus, isInProgress, nextStep, updatesFeed} from "../../../common/SACC/CareersFairContext/trackModel";
import {trackColours as c} from "../../../common/SACC/CareersFairContext/trackStyles";

const useStyles = makeStyles(() => ({
    page: {
        backgroundColor: c.cream,
        minHeight: "100vh",
        color: c.text,
        fontFamily: "Montserrat, sans-serif",
    },
    content: {
        maxWidth: "1280px",
        margin: "0 auto",
        padding: "64px 24px 80px",
    },
    heading: {
        fontSize: "48px",
        fontWeight: 700,
        letterSpacing: "-0.02em",
        color: c.green,
        margin: 0,
    },
    intro: {
        fontSize: "20px",
        color: c.muted,
        margin: "14px 0 0",
    },
    columns: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 400px",
        gap: "28px",
        alignItems: "start",
        marginTop: "28px",
        "@media (max-width: 1200px)": {
            gridTemplateColumns: "minmax(0, 1fr)",
        },
    },
    columnsSingle: {
        gridTemplateColumns: "minmax(0, 1fr)",
    },
    notice: {
        backgroundColor: "#E3F1E6",
        color: "#1D6B3A",
        borderRadius: "10px",
        padding: "12px 16px",
        fontSize: "16px",
        fontWeight: 600,
        margin: "0 0 18px",
    },
    summary: {
        fontSize: "16px",
        color: c.muted,
        margin: "0 0 18px",
    },
    list: {
        display: "flex",
        flexDirection: "column",
        gap: "18px",
    },
    panel: {
        backgroundColor: c.card,
        borderRadius: "16px",
        border: `1px solid ${c.border}`,
        boxShadow: "0 1px 3px rgba(20, 40, 31, 0.05)",
        padding: "28px",
        marginTop: "34px",
        "@media (max-width: 1200px)": {
            marginTop: 0,
        },
    },
    panelTitle: {
        fontSize: "20px",
        fontWeight: 700,
        color: c.green,
        margin: "0 0 4px",
    },
    update: {
        padding: "16px 0",
        borderBottom: `1px solid ${c.border}`,
        "&:last-child": {
            borderBottom: "none",
            paddingBottom: 0,
        },
    },
    updateDate: {
        fontSize: "15px",
        color: c.muted,
    },
    updateCompany: {
        fontSize: "16px",
        fontWeight: 700,
        margin: "6px 0 2px",
    },
    updateText: {
        fontSize: "16px",
        lineHeight: 1.55,
        margin: 0,
    },
    message: {
        backgroundColor: c.card,
        borderRadius: "16px",
        border: `1px solid ${c.border}`,
        padding: "40px 28px",
        textAlign: "center",
    },
    messageTitle: {
        fontSize: "22px",
        fontWeight: 700,
        color: c.green,
        margin: "0 0 10px",
    },
    messageText: {
        fontSize: "17px",
        color: c.muted,
        margin: "0 0 22px",
    },
    button: {
        display: "inline-block",
        borderRadius: "10px",
        padding: "11px 20px",
        fontSize: "16px",
        fontWeight: 700,
        textDecoration: "none",
        backgroundColor: c.green,
        color: "#FFFFFF",
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        "&:hover": {
            backgroundColor: c.greenHover,
        },
    },
}));

const statusOrder = [
    "ACTION REQUIRED - INTERVIEW",
    "INTERVIEW CONFIRMED",
    "INTERVIEW",
    "ACCEPTED",
    "SUBMITTED",
    "UNSUCCESSFUL"
];

function sortRank(application) {
    if (nextStep(application).needsYou) {
        return -1;
    }
    const index = statusOrder.indexOf(getDisplayStatus(application));
    return index === -1 ? statusOrder.length : index;
}

function summaryLine(applications) {
    const inProgress = applications.filter(isInProgress).length;
    const needsYou = applications.filter((a) => nextStep(a).needsYou).length;

    if (inProgress === 0) {
        return "No applications in progress";
    }
    return `${inProgress} in progress` + (needsYou > 0 ? `, ${needsYou} ${needsYou === 1 ? "needs" : "need"} you` : "");
}


const Track = (props) => {

    const {listApplication, companyInfo} = useAuth();

    const classes = useStyles();

    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [logos, setLogos] = useState({});
    const [notice, setNotice] = useState("");
    const [params, ] = useSearchParams();
    const highlightID = params.get("application");

    function updateApplication(applicationID, fields) {
        setApplications((current) => current.map((a) => a["application-id"] === applicationID ? {...a, ...fields} : a));
    }

    function removeApplication(applicationID) {
        const withdrawn = applications.find((a) => a["application-id"] === applicationID);
        setApplications((current) => current.filter((a) => a["application-id"] !== applicationID));
        setNotice(withdrawn ? `Your application for ${withdrawn["position-name"]} at ${withdrawn["company-name"]} has been withdrawn.` : "Your application has been withdrawn.");
    }

    function loadLogos(list) {
        const companyIDs = [...new Set(list.map((a) => a["company-id"]).filter(Boolean))];
        companyIDs.forEach((companyID) => {
            companyInfo(companyID).then(async (res) => {
                if (res.status === 200) {
                    const data = await res.json();
                    if (data.data["logo"]) {
                        setLogos((current) => ({...current, [companyID]: data.data["logo"]}));
                    }
                }
            }).catch((err) => console.warn(err));
        });
    }

    function load() {
        setLoading(true);
        setError("");

        listApplication().then(async (appRes) => {
            const data = await appRes.json();
            if (appRes.status === 200) {
                const list = data.data["applications"] ?? [];
                setApplications([...list].sort((a, b) => sortRank(a) - sortRank(b)));
                loadLogos(list);
            } else {
                setError(data.data["reason"] || "We were unable to load your applications.");
            }
        }).catch((err) => {
            console.warn(err);
            setError("We were unable to load your applications. Please check your connection and try again.");
        }).finally(() => {
            setLoading(false);
        })
    }

    useEffect(() => {
        load();
        props.setShowCover(false);

    }, [])

    const updates = updatesFeed(applications);
    const showList = !loading && error === "" && applications.length > 0;
    const highlightExists = applications.some((a) => a["application-id"] === highlightID);

    return (
        <div className={classes.page}>
            <main className={classes.content}>
                <h1 className={classes.heading}>My applications</h1>
                <p className={classes.intro}>Status and next step for each application. Every change is also emailed to you.</p>

                <div className={`${classes.columns} ${showList && updates.length > 0 ? "" : classes.columnsSingle}`}>
                    <section aria-label="Applications">
                        {notice !== "" && <p className={classes.notice} role="status">{notice}</p>}
                        {loading && (
                            <div className={classes.message}>
                                <h2 className={classes.messageTitle}>Loading...</h2>
                                <p className={classes.messageText}>Please wait as we load your applications</p>
                            </div>
                        )}
                        {!loading && error !== "" && (
                            <div className={classes.message}>
                                <h2 className={classes.messageTitle}>Something went wrong</h2>
                                <p className={classes.messageText}>{error}</p>
                                <button className={classes.button} onClick={load}>Try again</button>
                            </div>
                        )}
                        {!loading && error === "" && applications.length === 0 && (
                            <div className={classes.message}>
                                <h2 className={classes.messageTitle}>No applications yet</h2>
                                <p className={classes.messageText}>When you apply for a role, you can follow its progress here.</p>
                                <Link className={classes.button} to="/cfas/apply/home">Find roles</Link>
                            </div>
                        )}
                        {showList && (
                            <>
                                <p className={classes.summary}>{summaryLine(applications)}</p>
                                <div className={classes.list}>
                                    {applications.map((application, index) => (
                                        <TrackApplicationCard key={application["application-id"]}
                                                              application={application}
                                                              logo={logos[application["company-id"]]}
                                                              defaultOpen={highlightExists ? false : index === 0}
                                                              highlight={application["application-id"] === highlightID}
                                                              onUpdate={updateApplication}
                                                              onWithdrawn={removeApplication}/>
                                    ))}
                                </div>
                            </>
                        )}
                    </section>

                    {showList && updates.length > 0 && (
                        <aside className={classes.panel} aria-label="Updates">
                            <h2 className={classes.panelTitle}>Updates</h2>
                            {updates.map((update) => (
                                <div className={classes.update} key={update.key}>
                                    <div className={classes.updateDate}>{formatDate(update.date)}</div>
                                    <div className={classes.updateCompany}>{update.company}</div>
                                    <p className={classes.updateText}>{update.text}</p>
                                </div>
                            ))}
                        </aside>
                    )}
                </div>
            </main>
        </div>
    );
};

export const TrackViewApplicationRedirect = () => {
    const [params, ] = useSearchParams();
    const applicationID = params.get("applicationID");
    return <Navigate replace to={applicationID ? `/cfas/track/home?application=${encodeURIComponent(applicationID)}` : "/cfas/track/home"}/>;
};

export default Track;
